/**
 * Dev-only Clerk bypass. Lets you run the App Router PMS pages (under
 * `/dashboard/[workspace_id]/...`) without going through Clerk, by reading
 * a Seam session token directly from an env var or localStorage and
 * seeding it into the existing session-state store.
 *
 * ## Why
 *
 * Clerk is hard to point at a local backend, and the satellite domain
 * (`*.seam.vc`) round-trips through `console.seam.co` to mint cookies,
 * which makes pure-local PMS development painful. The bypass lets a
 * developer skip Clerk entirely and bring their own Seam session token.
 *
 * ## How to use (local dev only)
 *
 * 1. Grab a Seam session token from your own logged-in browser (DevTools
 *    → Network → any Seam request → Authorization header, or copy the
 *    `seam_session_token` cookie from a logged-in tab).
 * 2. Set the env vars in your shell or `.env.development.local`:
 *
 *        NEXT_PUBLIC_BYPASS_CLERK=true
 *        NEXT_PUBLIC_DEV_SEAM_SESSION_TOKEN=ey...     # optional
 *
 *    If you skip the second var, the hook falls back to
 *    `localStorage.dev_seam_token`, which you can set in DevTools.
 * 3. Run `pnpm dev:console`. The PMS routes (`/dashboard/...`) work; pages
 *    that talk directly to Clerk (`/signin`, `/workspaces/*`) won't.
 *
 * ## Prod safety
 *
 * Both `DEV_BYPASS_CLERK` and `getDevSeamToken` are hard-gated to
 * `process.env.NODE_ENV === "development"`. Next.js replaces `NODE_ENV`
 * at build time, so the bypass branches are dead code in production
 * bundles and get tree-shaken away.
 *
 * As a belt-and-braces guard, the module also throws at load time if it
 * detects someone trying to ship a prod build with the bypass env var
 * set — so an accidental Vercel env var can't slip past CI.
 */

if (
  process.env.NODE_ENV === "production" &&
  process.env.NEXT_PUBLIC_BYPASS_CLERK === "true"
) {
  // Fail loud at module load. The runtime check is redundant with the
  // NODE_ENV gate below (which the build will dead-code-eliminate), but
  // it catches the case where someone misconfigures the env var on a
  // production environment.
  throw new Error(
    "NEXT_PUBLIC_BYPASS_CLERK=true is not allowed in a production build. " +
      "This bypass is for local development only."
  )
}

export const DEV_BYPASS_CLERK =
  process.env.NODE_ENV === "development" &&
  process.env.NEXT_PUBLIC_BYPASS_CLERK === "true"

export function getDevSeamToken(): string | null {
  if (process.env.NODE_ENV !== "development") return null
  const fromEnv = process.env.NEXT_PUBLIC_DEV_SEAM_SESSION_TOKEN
  if (fromEnv != null && fromEnv !== "") return fromEnv
  if (typeof window === "undefined") return null
  try {
    const fromStorage = window.localStorage.getItem("dev_seam_token")
    if (fromStorage != null && fromStorage !== "") return fromStorage
  } catch {
    return null
  }
  return null
}
